import { FC } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Chip, IconButton, Tooltip, Box, Paper, Grid } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Quote } from '../types/api';
import { retrySyncQuote } from '../services/quotes.service';
import { dataTables } from '../styles';

interface Props {
  data: Quote[];
  pageSize: number;
  totalRows: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowClick?: (quote: Quote) => void;
  loading?: boolean;
  onRefresh?: () => void;
}

export const QuoteTable: FC<Props> = (props) => {
  const handleRetrySync = async (guid: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    try {
      await retrySyncQuote(guid);
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error) {
      console.error('Error retrying sync:', error);
      alert('Failed to retry sync: ' + (error as any).message);
    }
  };

  const getSyncStatusChip = (params: GridRenderCellParams) => {
    const { syncStatus, syncRetryCount, guid } = params.row;

    if (syncStatus === 'SYNCED') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Synced"
          color="success"
          size="small"
        />
      );
    } else if (syncStatus === 'FAILED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<ErrorIcon />}
            label="Failed"
            color="error"
            size="small"
          />
          {syncRetryCount < 3 && (
            <Tooltip title="Retry sync">
              <IconButton
                size="small"
                onClick={(e) => handleRetrySync(guid, e)}
                color="primary"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {syncRetryCount >= 3 && (
            <Tooltip title="Max retries reached">
              <IconButton size="small" disabled>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    } else {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Pending"
          color="warning"
          size="small"
        />
      );
    }
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
      DRAFT: 'default',
      SENT: 'info',
      ACCEPTED: 'success',
      DECLINED: 'warning',
      ARCHIVED: 'default',
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value / 100); // Convert pence to pounds
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'guid', headerName: 'GUID', width: 100 },
    {
      field: 'number',
      headerName: 'Quote Number',
      flex: 0.15,
      minWidth: 120,
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 0.2,
      minWidth: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.12,
      minWidth: 100,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'syncStatus',
      headerName: 'Sync Status',
      flex: 0.18,
      minWidth: 150,
      renderCell: getSyncStatusChip,
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 0.12,
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'issueDate',
      headerName: 'Issue Date',
      flex: 0.12,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'expiryDate',
      headerName: 'Expiry Date',
      flex: 0.12,
      minWidth: 120,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
  ];

  return (
    <Paper
      component="form"
      sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%' }}
    >
      <Grid container sx={{ width: '100%' }}>
        <Grid size={12}>
          <DataGrid
            rows={props.data}
            columns={columns}
            getRowId={(row) => row.guid || row.id || Math.random().toString()}
            paginationModel={{
              page: props.currentPage,
              pageSize: props.pageSize,
            }}
            rowCount={props.totalRows}
            paginationMode="server"
            pageSizeOptions={[25, 50, 100]}
            density="compact"
            autoHeight
            loading={props.loading}
            onPaginationModelChange={(model) => {
              props.onPageChange(model.page);
              props.onPageSizeChange(model.pageSize);
            }}
            onRowClick={(params) => {
              if (props.onRowClick) {
                const quote = props.data.find((q) => q.guid === params.row.guid);
                if (quote) {
                  props.onRowClick(quote);
                }
              }
            }}
            columnVisibilityModel={{
              id: false,
              guid: false,
            }}
            sx={dataTables}
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
